import type { DelegationValidateOptions, Policy, DelegationOptions, DelegationPayload, DID, DIDURL, StandardSchemaV1, DecodedEnvelope, DelegationFromOptions } from './types.js'
import type { CID } from 'multiformats'
import { base64pad } from 'iso-base/rfc4648'
import { parse as didParse } from 'iso-did'
import { randomBytes } from 'iso-web/crypto'
import * as Envelope from './envelope.js'
import {
    assertExpiration,
    assertIsValidCommand,
    assertMeta,
    assertNonce,
    assertNotBefore,
    assertNotRevoked,
    cid,
    expOrTtl,
    verifySignature,
} from './util.js'

/**
 * UCAN Delegation
 */
export class Delegation {
    iss:DIDURL
    aud:DID
    sub:DID | null
    cmd:string
    pol:Policy<unknown>
    nbf:number | undefined
    nonce:Uint8Array
    meta:Record<string, unknown> | undefined
    exp:number | null
    envelope:DecodedEnvelope<'dlg'>
    bytes:Uint8Array
    cid:CID

    constructor (envelope:DecodedEnvelope<'dlg'>, bytes:Uint8Array, cid:CID) {
        this.envelope = envelope
        this.bytes = bytes
        this.cid = cid

        this.iss = envelope.payload.iss
        this.aud = envelope.payload.aud
        this.sub = envelope.payload.sub ? envelope.payload.sub : null
        this.cmd = envelope.payload.cmd
        this.pol = envelope.payload.pol
        this.exp = envelope.payload.exp
        this.nbf = envelope.payload.nbf
        this.nonce = envelope.payload.nonce
        this.meta = envelope.payload.meta
    }

    /**
     * Create a delegation from bytes and validate it
     *
     * Does not validate "not before" time, call {@link Delegation.validate} to validate it
     */
    static async from (options:DelegationFromOptions):Promise<Delegation> {
        const { bytes, didResolver: didResolveOptions, verifierResolver } = options

        const envelope = Envelope.decode({ envelope: bytes }) as DecodedEnvelope<'dlg'>

        if (envelope.spec !== 'dlg') {
            throw new Error(
        `Invalid envelope type. Expected: Delegation but got: ${envelope.spec}`
            )
        }

        assertStructure(envelope.payload)

        await verifySignature(envelope, verifierResolver, didResolveOptions)

        const _cid = await cid(envelope)
        await assertNotRevoked(_cid, options.isRevoked)

        return new Delegation(envelope, bytes, _cid)
    }

    /**
     * Create a delegation from a base64 encoded string
     */
    static async fromString (token:string):Promise<Delegation> {
        const bytes = base64pad.decode(token)
        const envelope = Envelope.decode({ envelope: bytes }) as DecodedEnvelope<'dlg'>

        if (envelope.spec !== 'dlg') {
            throw new Error(
        `Invalid envelope type. Expected: Delegation but got: ${envelope.spec}`
            )
        }

        assertStructure(envelope.payload)
        const _cid = await cid(envelope)
        return new Delegation(envelope, bytes, _cid)
    }

    static async create<Schema extends StandardSchemaV1> (
        options:DelegationOptions<Schema>
    ):Promise<Delegation> {
        const nonce = options.nonce || randomBytes(12)

        const payload:DelegationPayload<StandardSchemaV1.InferOutput<Schema>> = {
            iss: options.iss.toString(),
            aud: options.aud,
            sub: options.sub ? options.sub : null,
            pol: options.pol,
            cmd: options.cmd,
            nonce,
            exp: expOrTtl(options),
        }

        if (options.nbf) {
            payload.nbf = options.nbf
        }

        if (options.meta) {
            payload.meta = options.meta
        }

        assertStructure(payload)

        const { signature, signaturePayload } = await Envelope.sign({
            spec: 'dlg',
            signer: options.iss,
            payload,
        })

        const bytes = Envelope.encode({ signature, signaturePayload })

        const envelope:DecodedEnvelope<'dlg'> = {
            alg: options.iss.signatureType,
            enc: 'DAG-CBOR',
            signature,
            payload,
            spec: 'dlg',
            version: Envelope.VERSION,
        }

        return new Delegation(envelope, bytes, await cid(envelope))
    }

    /**
     * Validate a delegation
     *
     * Checks the structure, "not before" time, signature, and revocation status
     */
    async validate (options:DelegationValidateOptions):Promise<boolean> {
        assertStructure(this.envelope.payload)
        assertNotBefore(this.envelope.payload.nbf)
        await verifySignature(
            this.envelope,
            options.verifierResolver,
            options.didResolver
        )
        await assertNotRevoked(this.cid, options.isRevoked)

        return true
    }

    toString ():string {
        return base64pad.encode(this.bytes)
    }

    toJSON () {
        return {
            token: this.toString(),
            cid: this.cid.toString(),
            envelope: {
                payload: {
                    iss: this.iss.toString(),
                    aud: this.aud.toString(),
                    sub: this.sub ? this.sub.toString() : null,
                    cmd: this.cmd,
                    pol: this.pol,
                    exp: this.exp,
                    nbf: this.nbf,
                    nonce: base64pad.encode(this.nonce),
                    meta: this.meta,
                },
                signature: base64pad.encode(this.envelope.signature),
                alg: this.envelope.alg,
                enc: this.envelope.enc,
                spec: this.envelope.spec,
                version: this.envelope.version,
            },
        }
    }
}

function assertStructure (payload:DelegationPayload<unknown>):void {
    didParse(payload.iss)
    didParse(payload.aud)

    if (payload.sub !== null) {
        didParse(payload.sub)
    }

    assertIsValidCommand(payload.cmd)
    assertExpiration(payload.exp)
    assertNonce(payload.nonce)

    // assertPolicy(payload.pol)

    assertMeta(payload.meta)
}
